import {
    DataGridPro,
    useGridApiRef,
    type GridSortModel
} from '@mui/x-data-grid-pro';
import { Box, CircularProgress, Button, Typography, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from "../contexts/GlobalState";
import { useCallback, useEffect, useState } from 'react';
import { getAllTemplates } from '../api/vegApl';
import { getLabelTemplateCols, type LabelTemplateGridRow } from '../utils/LabelTemplateGridConfig';
import { useCommonStyles } from '../styles/GenericStyle';
import { getDataGridStyles } from '../styles/MtComStyleObj';
import { DASH_PLACEHOLDER } from '../utils/missingValue';
import { useResponsiveHeaderHeight } from '../hooks/useResponsiveHeaderHeight';
import { usePersistedGridState } from '../hooks/usePersistedGridState';
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";

export const LabelTemplates = () => {
    const navigate = useNavigate();
    const headerHeight = useResponsiveHeaderHeight(48);
    const classes = useCommonStyles();
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange } = usePersistedGridState('label-template-master-grid');
    const { setBreadcrumbsText, crops } = useGlobalState();
    const [isLoading, setIsLoading] = useState(false);
    const [rows, setRows] = useState<LabelTemplateGridRow[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'template_name', sort: 'asc' }]);

    const mapTemplateRows = (data: any[]): LabelTemplateGridRow[] => {
        return (data ?? []).map((item: any, index: number) => {
            const width = item?.width ?? item?.template_width;
            const height = item?.height ?? item?.template_height;
            const sizeValue = item?.size || item?.template_size || ((width && height) ? `${width} x ${height}` : '');
            const siteCode =
                item?.site_code ||
                item?.siteCode ||
                item?.site ||
                item?.site_name ||
                item?.siteName ||
                item?.site_details?.site_code ||
                '';
            const rawDataPoints = Array.isArray(item?.data_points)
                ? item?.data_points?.join(', ')
                : (item?.data_points || item?.dataPoints || '');
            // SAPNR is never shown on label templates, for any site.
            const dataPoints = rawDataPoints
                .split(',')
                .map((entry: string) => entry.trim())
                .filter((entry: string) => entry.toLowerCase() !== 'sapnr')
                .join(', ');

            return {
                id: item?.template_id || item?.label_template_id || item?.id || `template-${index}`,
                template_name: item?.template_name || item?.templateName || item?.name || DASH_PLACEHOLDER,
                type: item?.type || item?.template_type || item?.templateType || DASH_PLACEHOLDER,
                size: sizeValue || DASH_PLACEHOLDER,
                orientation: item?.orientation || DASH_PLACEHOLDER,
                site_code: siteCode || DASH_PLACEHOLDER,
                crop_code:
                    item?.crop_code ||
                    item?.cropCode ||
                    item?.crop ||
                    item?.crop_name ||
                    item?.cropName ||
                    item?.crop_details?.crop_code ||
                    DASH_PLACEHOLDER,
                data_points: dataPoints || DASH_PLACEHOLDER,
                is_default: item?.isdefault ?? item?.is_default ?? item?.isDefault ?? false,
            };
        });
    };

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    }, []);

    const { mutate: getAllTemplatesCall } = getAllTemplates({
        mutation: {
            onSuccess: (response) => {
                const templates = response?.data ?? [];
                setRows(mapTemplateRows(templates));
                setIsLoading(false);
            },
            onError: () => {
                setRows([]);
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        setBreadcrumbsText(["Home", "Post-Season Module", "Label Templates"]);
        setIsLoading(true);
        getAllTemplatesCall({} as any);
    }, []);

    return (
        <div data-testid="LabelTemplatesPage">
            <Box sx={{ px: 2, pt: 2, pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }} onClick={() => navigate(-1)}>
                    <ArrowBackIcon sx={{ fontSize: 20, color: '#000000' }} />
                    <Typography className={classes.labelNoto20Bold600}>Back</Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 3 }}>
                    <Typography className={classes.labelNoto36Bold700} sx={{ color: '#1E1E1E' }}>
                        Label Template Master
                    </Typography>
                    <Tooltip title="Action not ready. Under development." arrow placement="top">
                        <span>
                            <Button
                                variant="contained"
                                disableRipple
                                disabled
                                startIcon={<AddIcon />}
                                className={classes.greenButton}
                                sx={{ minWidth: 170, height: 44, borderRadius: '8px', fontWeight: 700, fontSize: 16, width: { xs: '100%', sm: 'auto' } }}
                            >
                                Add Template
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: 'flex', placeItems: 'center', justifyContent: 'center', height: '50vh' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <DataGridPro
                            rows={rows}
                            columns={applyColumnWidths(getLabelTemplateCols(classes, sortModel, crops))}
                            pageSizeOptions={[20, 50, 100, 500]}
                            columnHeaderHeight={headerHeight}
                            getRowHeight={() => 'auto'}
                            disableRowSelectionOnClick
                            checkboxSelection
                            pagination
                            apiRef={apiRef}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            onColumnWidthChange={onColumnWidthChange}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 50, page: 0 } },
                            }}
                            localeText={{
                                noRowsLabel: 'No Label Templates found',
                                noResultsOverlayLabel: 'No Label Templates found',
                            }}
                            slotProps={{
                                baseCheckbox: {
                                    checkedIcon: <Box component="img" alt="" src="icons/CheckBox.svg" />,
                                    icon: <CheckBoxOutlineBlankRoundedIcon fontSize="small" />,
                                    sx: {
                                        "&.Mui-checked": { color: '#000000' },
                                        color: '#000000',
                                        fontSize: 20,
                                        "& svg": { fontSize: 20 },
                                    },
                                },
                            }}
                            sx={(theme) => getDataGridStyles(theme, {
                                headerColor: '#DEDEDE',
                                fillerColor: '#DEDEDE',
                                maxHeight: 'calc(100vh - 290px)',
                            })}
                        />
                    </Box>
                )}
            </Box>
        </div>
    );
};
